"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Suite = {
  number: string;
  numeral: string;
  name: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  detailImage: string;
  detailSrcset: string;
  detailSizes: string;
  detailAlt: string;
  pieces: string;
  piece1: string;
  piece2: string;
  piece3: string;
  narrative: string;
};
const suites: Suite[] = [{
  number: "01",
  numeral: "No. I",
  name: "Budowa Domów",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/budowa_domu_jednorodzinnego.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/budowa_domu_jednorodzinnego.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/budowa_domu_jednorodzinnego.png-wvc-sizes",
  alt: "Nowoczesny dom jednorodzinny w trakcie budowy",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_zbrojenia.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_zbrojenia.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_zbrojenia.png-wvc-sizes",
  detailAlt: "Precyzyjne zbrojenie fundamentów",
  pieces: "Kompleksowa realizacja inwestycji",
  piece1: "Budowa domów w stanie surowym",
  piece2: "Wykończenia do stanu deweloperskiego",
  piece3: "Nadzór i koordynacja prac",
  narrative: "Realizujemy marzenia o własnym domu z najwyższą dbałością o detale konstrukcyjne. Od fundamentów po dach, zapewniamy solidność i terminowość na każdym etapie budowy."
}, {
  number: "02",
  numeral: "No. II",
  name: "Roboty Ziemne",
  image: "https://budsys.pl/wp-content/uploads/2026/07/b0ba077d06c6f7371731576f7f13fac0.jpg?v=c7whh7jrjfs",
  srcset: "",
  sizes: "",
  alt: "Mała koparka kopacząca fundament pod budynek z widoku kabiny operatora",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_maszyny_budowlanej.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_maszyny_budowlanej.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_maszyny_budowlanej.png-wvc-sizes",
  detailAlt: "Profesjonalny sprzęt budowlany w akcji",
  pieces: "Przygotowanie terenu pod budowę",
  piece1: "Wykopy pod fundamenty i piwnice",
  piece2: "Niwelacja i wyrównywanie terenu",
  piece3: "Przygotowanie działki pod inwestycję",
  narrative: "Solidne fundamenty to podstawa każdej budowli. Nasz park maszynowy pozwala na precyzyjne wykonanie wykopów oraz profesjonalną niwelację terenu na terenie całego Śląska."
}, {
  number: "03",
  numeral: "No. III",
  name: "Kostka Brukowa",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ukladanie_kostki_brukowej.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ukladanie_kostki_brukowej.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/ukladanie_kostki_brukowej.png-wvc-sizes",
  alt: "Profesjonalne układanie kostki brukowej na podjeździe",
  detailImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_bruku.png",
  detailSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_bruku.png-wvc-srcset",
  detailSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723616/img/detal_bruku.png-wvc-sizes",
  detailAlt: "Precyzyjne ułożenie kostki brukowej",
  pieces: "Estetyczne zagospodarowanie terenu",
  piece1: "Podjazdy i chodniki z kostki",
  piece2: "Parkingi i place manewrowe",
  piece3: "Tarasy i opaski wokół budynków",
  narrative: "Tworzymy trwałe i estetyczne nawierzchnie, które podnoszą wartość każdej nieruchomości. Nasze prace brukarskie to połączenie funkcjonalności z nowoczesnym designem."
}];
function SuiteBlock({
  suite,
  index
}: {
  suite: Suite;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isOdd = index % 2 === 0;
  return <div ref={ref} data-index={index} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`relative lg:col-span-7 ${isOdd ? "lg:order-1" : "lg:order-2"}`}>
        <div className="relative overflow-hidden rounded-lg">
          <img src={suite.image} data-wvc-srcset={suite.srcset} data-wvc-sizes={suite.sizes} alt={suite.alt} loading="lazy" decoding="async" className="w-full h-[480px] md:h-[600px] lg:h-[680px] object-cover" />
          <div className="absolute inset-0 bg-blue-900/10 mix-blend-multiply pointer-events-none" />
        </div>

        <div className={`hidden md:block absolute ${isOdd ? "-right-6 lg:-right-10 bottom-16" : "-left-6 lg:-left-10 bottom-16"} w-36 h-36 lg:w-44 lg:h-44 overflow-hidden rounded-lg shadow-md ring-1 ring-slate-200`}>
          <img src={suite.detailImage} data-wvc-srcset={suite.detailSrcset} data-wvc-sizes={suite.detailSizes} alt={suite.detailAlt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className={`lg:col-span-5 flex flex-col justify-center ${isOdd ? "lg:order-2" : "lg:order-1"}`}>
        <div className="font-default text-[64px] md:text-[80px] leading-none text-slate-900/0 mb-6" style={{
        WebkitTextStroke: "1px var(--foreground)"
      }}>
          {suite.numeral}
        </div>

        <h3 className="font-default font-bold text-3xl md:text-4xl lg:text-[44px] leading-tight text-slate-900 mb-8">
          {suite.name}
        </h3>

        <div className="font-mono text-xs tracking-[0.2em] uppercase text-slate-500 mb-3">
          Zakres prac
        </div>
        <p className="font-default text-base text-slate-600 italic mb-5">
          {suite.pieces}
        </p>

        <ul className="space-y-2.5 mb-8">
          <li className="font-default text-base text-slate-900 flex gap-3">
            <span className="text-blue-600 mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 rounded-md bg-blue-600" />
            </span>
            <span>{suite.piece1}</span>
          </li>
          <li className="font-default text-base text-slate-900 flex gap-3">
            <span className="text-blue-600 mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 rounded-md bg-blue-600" />
            </span>
            <span>{suite.piece2}</span>
          </li>
          <li className="font-default text-base text-slate-900 flex gap-3">
            <span className="text-blue-600 mt-2 shrink-0">
              <span className="block w-1.5 h-1.5 rounded-md bg-blue-600" />
            </span>
            <span>{suite.piece3}</span>
          </li>
        </ul>

        <p className="font-default text-[17px] leading-relaxed text-slate-700 mb-10 max-w-xl">
          {suite.narrative}
        </p>

        <div className="flex flex-wrap items-center gap-6">
          <Link to="/OfferPage" className="group inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-slate-900">
            <span className="relative">
              Zobacz ofertę
              <span className="absolute left-0 -bottom-1 h-px w-full bg-slate-900 origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500 ease-out" />
              <span className="absolute left-0 -bottom-1 h-px w-full bg-blue-600 origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out delay-100" />
            </span>
            <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>

          <Button asChild size="lg" className="rounded-lg px-7 h-11 font-mono text-xs tracking-[0.2em] uppercase bg-blue-600 text-white hover:bg-blue-700">
            <Link to="/PricingPage">Bezpłatna wycena</Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function Construction() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="5009" id="budowa-domów-i-garaży" className="bg-white text-slate-900 pt-[100px] pb-32 md:pb-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-24 md:mb-32 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-12">
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-blue-600 mb-8">
              BudSys — Kompleksowe Inwestycje
            </div>
            <h1 className="font-default font-extrabold text-5xl sm:text-6xl md:text-7xl lg:text-[96px] xl:text-[120px] leading-[0.95] tracking-tight text-slate-900 mb-10">
              Budujemy
              <br />
              <span className="italic text-blue-600">solidne fundamenty.</span>
            </h1>
            <p className="font-default text-lg md:text-[19px] leading-relaxed text-slate-600 max-w-[540px]">
              Jesteśmy generalnym wykonawcą inwestycji budowlanych na terenie województwa śląskiego. Od przygotowania działki, przez budowę domu, aż po zagospodarowanie terenu — zapewniamy profesjonalizm i terminowość.
            </p>
          </div>
        </div>

        <div className="space-y-32 md:space-y-44 lg:space-y-[180px]">
          {suites.map((suite, i) => <div key={suite.number} data-index={i}>
              <SuiteBlock suite={suite} index={i} />
              {i < suites.length - 1 && <div className="mt-32 md:mt-44 lg:mt-[180px] -mb-32 md:-mb-44 lg:-mb-[180px]">
                  <Separator className="bg-slate-200" />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}